// apps/web/src/components/landing-v2/proofs/MoonProof.tsx
import type { CSSProperties } from 'react'

/** Theme 02 / Moon calendar — 3-week grid of moon-phase glyphs.
 *  Reference: handoff .mm-moon. */

type Phase = 'new' | 'cresc' | 'half' | 'gibb' | 'full' | 'today'

const DAYS: { phase: Phase; label?: string }[] = [
  { phase: 'new' }, { phase: 'cresc' }, { phase: 'cresc' }, { phase: 'half' },
  { phase: 'half' }, { phase: 'gibb' }, { phase: 'gibb' }, { phase: 'gibb' },
  { phase: 'full' }, { phase: 'today', label: '14' }, { phase: 'gibb' },
  { phase: 'gibb' }, { phase: 'half' }, { phase: 'cresc' }, { phase: 'cresc' },
  { phase: 'new' }, { phase: 'new' }, { phase: 'cresc' }, { phase: 'cresc' },
  { phase: 'half' }, { phase: 'gibb' },
]

const phaseDotStyle = (phase: Phase): CSSProperties => {
  switch (phase) {
    case 'new':
      return {
        background: 'var(--color-cosmic-black)',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.2)' /* theme-audit: out-of-band white α=0.2, not a hairline */,
      }
    case 'cresc':
      return {
        background: 'var(--color-text-primary)',
        clipPath: 'ellipse(45% 50% at 65% 50%)',
      }
    case 'half':
      return {
        background: 'linear-gradient(90deg, var(--color-cosmic-black) 50%, var(--color-text-primary) 50%)',
      }
    case 'gibb':
      return {
        background: 'var(--color-text-primary)',
        clipPath: 'ellipse(80% 50% at 60% 50%)',
      }
    case 'full':
      return {
        background: 'var(--color-text-primary)',
        boxShadow: '0 0 12px rgba(255,255,255,0.4)' /* theme-audit: out-of-band white glow α=0.4, not a hairline */,
      }
    default:
      return {}
  }
}

export function MoonProof() {
  return (
    <div className="absolute inset-0 grid grid-cols-7 content-center gap-1.5 p-5">
      {DAYS.map((d, i) => {
        const isToday = d.phase === 'today'
        return (
          <div
            key={i}
            className="relative flex aspect-square items-center justify-center rounded-md text-[9px]"
            style={
              isToday
                ? {
                    background: 'color-mix(in oklab, var(--color-astral-violet) 20%, transparent)',
                    border: '1px solid color-mix(in oklab, var(--color-astral-violet) 50%, transparent)',
                    color: 'var(--color-astral-violet-light)',
                  }
                : {
                    background: 'var(--hairline-faint)',
                    color: 'var(--color-text-muted)',
                  }
            }
          >
            {d.label ?? ''}
            {!isToday && (
              <span
                className="absolute bottom-[3px] right-[3px] h-[60%] w-[60%] rounded-full"
                style={phaseDotStyle(d.phase)}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
